/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, ReactNode} from 'react';
import {CheckboxProps} from '@material-ui/core';
import MuiCheckbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

export interface CheckboxTypes extends CheckboxProps {
  label?: string | JSX.Element;
  labelProps?: boolean;
  customLabel?: object;
  name?: string;
  labelwidth?: string;
  addRightMargin?: boolean;
  onChange?: (e: ChangeEvent<{name?: string; value: unknown;}>, child?: ReactNode) => void;
}

const useStyles = makeStyles(theme => ({
  root : {
    '&:hover' : {
      backgroundColor : 'rgba(0,0,0,0.08)'
    },
    '&$checked' : {
      '&:hover' : {
        backgroundColor : dlsAlt('components', 'checkBox', 'hoverBackground')
      }
    },
    '&.Mui-focusVisible' : {
      outline : `2px solid ${dlsColor('caBlue')}`
    }
  },
  checked       : {},
  rippleVisible : {
    opacity   : 0.01,
    animation : '$mui-ripple-enter 100ms cubic-bezier(0.4, 0, 0.2, 1)'
  },
  '@keyframes mui-ripple-enter' : {
    '0%' : {
      opacity : 0.01
    },
    '100%' : {
      opacity : 0.01
    }
  },
  icon : {
    borderRadius : 2,
    width        : 18,
    height       : 18,
    boxShadow    : `inset 0 0 0 1px ${dlsColor('grey3')}, inset 0 -1px 0 ${dlsColor(
      'grey3'
    )}`,
    'input:disabled ~ &' : {
      boxShadow : `inset 0 0 0 1px ${dlsColor(
        'grey4'
      )}, inset 0 -1px 0 ${dlsColor('grey4')}`
    }
  },
  checkedIcon : {
    backgroundColor : dlsColor('caBlue'),
    borderRadius    : 2,
    boxShadow       : `inset 0 0 0 1px ${dlsColor(
      'caBlue'
    )}, inset 0 -1px 0 ${dlsColor('caBlue')}`,
    '&:before' : {
      display         : 'block',
      width           : 18,
      height          : 18,
      backgroundImage :
        'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3E%3Cpath' +
        ' fill-rule=\'evenodd\' clip-rule=\'evenodd\' d=\'M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 ' +
        '1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z\' fill=\'%23fff\'/%3E%3C/svg%3E")',
      content : '""'
    },
    'input:hover ~ &' : {
      backgroundColor : dlsColor('caBlue')
    },
    'input:disabled ~ &' : {
      backgroundColor : dlsColor('grey4'),
      boxShadow       : `inset 0 0 0 1px ${dlsColor(
        'grey4'
      )}, inset 0 -1px 0 ${dlsColor('grey4')}`
    }
  },
  indeterminateIcon : {
    '&:before' : {
      backgroundImage :
        'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath' +
        ' fill-rule=\'evenodd\' clip-rule=\'evenodd\' d=\'M19 13H5v-2h14v2z\' fill=\'%23fff\'/%3E%3C/svg%3E")'
    }
  },
  label : ({
    customLabel,
    labelwidth
  }: {
    customLabel?: object,
    labelwidth?: string
  }) => customLabel || {
    ...dlsAlt('components', 'dropdown'),
    color         : dlsColor('grey2'),
    textTransform : 'none',
    textOverflow  : 'ellipsis',
    whiteSpace    : 'nowrap',
    overflow      : 'hidden',
    width         : labelwidth || 'auto'
  },
  removeMargin : ({addRightMargin}: CheckboxTypes) => (
    !addRightMargin ? {marginRight: '0px'} : undefined
  )
}));

/**
 *
 * @CheckBox
 *
 * Primary buttons are used to convey the most urgent or main actions on a particular
 * page. In cards, use the regular primary buttons. If paired, use it with the tertiary
 * button.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {CheckBox} from '@ion/dls/src/checkbox';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *      <>
 *        <CheckBox /> Default
 *        <br />
 *        <CheckBox disabled /> Disabled
 *      </>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
const CheckBox = ({
  label,
  labelProps,
  name,
  onChange,
  addRightMargin = true,
  ...props
}: CheckboxTypes) => {
  const classes = useStyles({...props, addRightMargin});
  const {customLabel, ...rest} = props;

  function _onChange(e: ChangeEvent<{name?: string; value: unknown;}>, child: ReactNode) {
    if (name) {
      e.target.name = name;
    }
    onChange?.(e, child);
  }
  return (
    <FormControlLabel classes={{
      label : labelProps ? classes.label : '',
      root  : classes.removeMargin
    }}
    control={
      <MuiCheckbox className={classes.root}
        classes={{
          root    : classes.root,
          checked : classes.checked
        }}
        disableRipple
        checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
        indeterminateIcon={<span className={clsx(classes.icon,
          classes.checkedIcon, classes.indeterminateIcon)} />}
        icon={<span className={classes.icon} />}
        color='primary'
        onChange={onChange ? _onChange : null}
        {...rest} />
    } label={label} />
  );
};

export default CheckBox;
